import { ApplicationEnum } from "~/@types/enums";
import rankByLabel, { normalize } from "~/helpers/search/labelMatcher";
import { buildDockPageEntries } from "~/helpers/search/pageSearchIndex";
import { resolveLocationMapLabel } from "~/layout/useModuleBranding";
import { getFeaturePageMapping, getFlatPageMapping, getPageInfo, systemPageMapping } from "~/locationMaps";

export interface NavigationDestination {
	label: string;
	labelKey?: string;
	path: string;
}

const destinationLabel = (destination: NavigationDestination): string =>
	resolveLocationMapLabel(destination.path, destination.label, destination.labelKey);

const stripPath = (path: string): string => path.trim().split(/[?#]/)[0];

const isExternalPath = (path: string): boolean => /^https?:\/\//.test(path);

const isPagePath = (path: string): boolean => /^\/[a-zA-Z0-9\-_/]*$/.test(path) && path !== "/Forbidden";

const flattenMappedPages = (mapping: { [key: string]: PageMapping }): NavigationDestination[] =>
	Object.entries(mapping).flatMap(([path, page]) => {
		if (page.children) return flattenMappedPages(page.children);
		if (!page.label) return [];
		return [
			{
				label: page.title || page.label,
				labelKey: page.title ? page.titleKey : page.labelKey,
				path: page.linkOut && isExternalPath(page.linkOut) ? page.linkOut : path
			}
		];
	});

const buildDestinationIndex = (): NavigationDestination[] => {
	const applications = Object.values(ApplicationEnum);
	const pages = [
		...flattenMappedPages(systemPageMapping),
		...applications.flatMap((application) => flattenMappedPages(getFeaturePageMapping(application))),
		...applications.flatMap((application) => flattenMappedPages(getFlatPageMapping(application))),
		...buildDockPageEntries().map(({ label, labelKey, path }) => ({ label, labelKey, path }))
	];
	const seen = new Set<string>();
	return pages.filter(({ path }) => {
		if (seen.has(path) || !(isPagePath(path) || isExternalPath(path))) return false;
		seen.add(path);
		return true;
	});
};

let cachedIndex: NavigationDestination[] | null = null;
const getDestinationIndex = (): NavigationDestination[] => (cachedIndex ??= buildDestinationIndex());

const isKnownDestinationPath = (path: string): boolean => {
	const pathname = stripPath(path);
	if (!pathname) return false;
	if (getDestinationIndex().some((destination) => destination.path === pathname)) return true;
	for (const application of Object.values(ApplicationEnum)) {
		if (getPageInfo(pathname, getFlatPageMapping(application))?.label) return true;
	}
	return false;
};

const moduleTrail = (path: string): string[] =>
	path
		.split("/")
		.filter(Boolean)
		.slice(0, -1)
		.map((segment) => segment.replace(/-/g, " ").toUpperCase());

const candidateLabel = (candidate: NavigationDestination, candidates: NavigationDestination[]): string => {
	const label = destinationLabel(candidate);
	const isShared = candidates.some(
		(other) => other.path !== candidate.path && normalize(destinationLabel(other)) === normalize(label)
	);
	return isShared ? [...moduleTrail(candidate.path), label].join(" › ") : label;
};

export type DestinationMatch =
	| { destination: NavigationDestination; kind: "exact" }
	| { candidates: NavigationDestination[]; query: string; kind: "list" }
	| { kind: "none"; query: string };

const MIN_PARTIAL_LABEL_LENGTH = 4;
const MAX_CANDIDATES = 3;

const groupByLabel = (destinations: NavigationDestination[]): Map<string, NavigationDestination[]> => {
	const groups = new Map<string, NavigationDestination[]>();
	for (const destination of destinations) {
		const key = normalize(destinationLabel(destination));
		const bucket = groups.get(key);
		if (bucket) bucket.push(destination);
		else groups.set(key, [destination]);
	}
	return groups;
};

const uniqueLabelMatch = (
	groups: Map<string, NavigationDestination[]>,
	label: string
): NavigationDestination | undefined => {
	const bucket = groups.get(label);
	return bucket && bucket.length === 1 ? bucket[0] : undefined;
};

const rankCandidates = (destinations: NavigationDestination[], query: string): NavigationDestination[] =>
	rankByLabel(destinations, destinationLabel, query, {
		limit: MAX_CANDIDATES,
		minLabelLength: MIN_PARTIAL_LABEL_LENGTH,
		shouldRequireAllWords: false
	}).map((match) => match.item);

const resolveNavigationTarget = (rawQuery: string, destinations = getDestinationIndex()): DestinationMatch => {
	const query = normalize(rawQuery);
	if (!query) return { kind: "none", query: rawQuery };

	const groups = groupByLabel(destinations);

	const exact = uniqueLabelMatch(groups, query);
	if (exact) return { destination: exact, kind: "exact" };

	const candidates = rankCandidates(destinations, rawQuery);
	if (candidates.length > 0) return { candidates, kind: "list", query: rawQuery };

	return { kind: "none", query: rawQuery };
};

export {
	buildDestinationIndex,
	candidateLabel,
	destinationLabel,
	getDestinationIndex,
	isExternalPath,
	isKnownDestinationPath,
	resolveNavigationTarget
};
export default resolveNavigationTarget;
